import emailjs from '@emailjs/browser';
import { supabase } from '../lib/supabase';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

console.log('EmailService initialized with:', { 
  hasServiceId: !!SERVICE_ID, 
  hasTemplateId: !!TEMPLATE_ID, 
  hasPublicKey: !!PUBLIC_KEY 
});

/**
 * emailService - Handles sending automated notifications via EmailJS
 */
export const emailService = {
  /**
   * Sends a registration status notification email
   * @param {Object} params - { name, role, status, reason, to_email }
   */
  sendStatusEmail: async ({ name, role, status, reason, to_email }) => {
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      console.warn('EmailJS credentials missing. Email not sent.');
      return { success: false, error: 'Credentials missing' };
    }

    try {
      const templateParams = {
        name,
        role,
        status: status === 'active' ? 'Approved' : 'Rejected',
        reason: reason || 'No specific reason provided.',
        to_email,
        login_url: window.location.origin + '/login',
      };

      console.log('Attempting to send email via EmailJS with params:', templateParams);
      console.log('Using Service ID:', SERVICE_ID, 'Template ID:', TEMPLATE_ID);

      const response = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
      console.log('Email sent successfully. Response:', response);
      return { success: true };
    } catch (error) {
      // Improved error logging for EmailJS failures
      console.error('EmailJS Send Failed:', {
        status: error?.status,
        text: error?.text,
        message: error?.message || 'Unknown error',
        errorObj: error
      });
      return { 
        success: false, 
        error: error?.text || error?.message || 'Failed to send email' 
      };
    }
  },

  /**
   * Sends a bill notification email
   * @param {Object} bill - The bill object
   */
  sendBillEmail: async (bill) => {
    const billTemplateId = process.env.REACT_APP_EMAILJS_BILL_TEMPLATE_ID || TEMPLATE_ID;
    if (!SERVICE_ID || !billTemplateId || !PUBLIC_KEY) {
      return { success: false, error: 'Credentials missing' };
    }

    const to_email = bill.resident?.profile?.email;
    if (!to_email) return { success: false, error: 'Resident has no email' };

    const name = bill.resident?.profile?.name || bill.resident?.house_number || 'Resident';
    const monthStr = new Date(bill.billing_period_year, bill.billing_period_month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });

    try {
      const templateParams = {
        name,
        to_email,
        bill_month: monthStr,
        total_amount: `$${Number(bill.total_amount).toFixed(2)}`,
        login_url: window.location.origin + '/login',
        message: `Your waste segregation bill for ${monthStr} is ready. Total amount due: $${Number(bill.total_amount).toFixed(2)}. Please log in to your dashboard to view the full receipt and make a payment.`
      };

      const response = await emailjs.send(SERVICE_ID, billTemplateId, templateParams, PUBLIC_KEY);
      return { success: true };
    } catch (error) {
      console.error('EmailJS Bill Send Failed:', error);
      return { success: false, error: error?.text || 'Failed to send bill email' };
    }
  },

  /**
   * Sends a missed collection notification email to a collector
   * @param {Object} params - { to_email, name, missedCategories, date }
   */
  sendMissedCollectionEmail: async ({ to_email, name, missedCategories, date }) => {
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      console.warn('EmailJS credentials missing. Missed-collection email not sent.');
      return { success: false, error: 'Credentials missing' };
    }

    if (!to_email) return { success: false, error: 'No email address provided' };

    try {
      const templateParams = {
        name: name || 'Collector',
        to_email,
        status: 'Missed Collection Alert',
        reason: `The following waste categories were not collected on ${date}: ${missedCategories}. Please address this as soon as possible.`,
        message: `⚠️ Missed Collection Alert\n\nDear ${name || 'Collector'},\n\nThis is an automated notification that the following waste categories were NOT collected on ${date}:\n\n${missedCategories}\n\nPlease take immediate action to address these missed collections. Log in to your dashboard for more details.`,
        login_url: window.location.origin + '/login',
      };

      console.log('Sending missed-collection email to:', to_email);
      const response = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
      console.log('Missed-collection email sent successfully to:', to_email);
      return { success: true };
    } catch (error) {
      console.error('EmailJS Missed Collection Send Failed:', {
        to_email,
        status: error?.status,
        text: error?.text,
        message: error?.message || 'Unknown error',
      });
      return {
        success: false,
        error: error?.text || error?.message || 'Failed to send missed-collection email',
      };
    }
  },

  /**
   * Sends a contact form submission email
   * @param {Object} params - { name, email, subject, message }
   */
  sendContactEmail: async ({ name, email, subject, message }) => {
    const contactTemplateId = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID || TEMPLATE_ID;
    if (!SERVICE_ID || !contactTemplateId || !PUBLIC_KEY) {
      console.warn('EmailJS credentials missing. Contact email not sent.');
      return { success: false, error: 'Credentials missing' };
    }

    // 1. Fetch super admin emails dynamically from Supabase bypass policy
    let adminEmails = ['support@smartwaste.co.ke'];
    try {
      const { data, error } = await supabase.rpc('get_admin_emails');
      if (data && data.length > 0) {
        adminEmails = data.map(item => item.email);
        console.log('Fetched admin emails for contact form routing:', adminEmails);
      }
    } catch (dbErr) {
      console.warn('Bypassing admin DB fetch, using default support email:', dbErr);
    }

    try {
      // 2. Loop send email to all admin emails
      const sendPromises = adminEmails.map(adminEmail => {
        const templateParams = {
          name,
          from_name: name,
          from_email: email,
          email,
          subject,
          message,
          to_email: adminEmail
        };
        console.log('Sending contact form email to admin:', adminEmail);
        return emailjs.send(SERVICE_ID, contactTemplateId, templateParams, PUBLIC_KEY);
      });

      const responses = await Promise.all(sendPromises);
      console.log('Contact form email sent successfully to all admins. Responses:', responses);
      return { success: true };
    } catch (error) {
      console.error('EmailJS Contact Send Failed:', error);
      return {
        success: false,
        error: error?.text || error?.message || 'Failed to send contact email'
      };
    }
  }
};
